// SCR-STAFF-960 جزئیات سرنخ (AP-ENG-01؛ US-ENG-001 تا 005): اطلاعات، برچسب «مراجعه‌کننده فعلی»، تاریخچه منابع (ادغام‌ها)، مشاوره‌ها و پیگیری‌ها؛ اقدام‌ها:
// ثبت تماس، «ثبت مراجعه‌کننده و رزرو» (فرم ثبت سریع پیش‌پر FLOW-01 ← تبدیل ← رزرو FLOW-02؛ AC-ENG-005.1)، Lost با علت (AC-ENG-005.2) و بازگشت.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type EngLeadView } from '@phoenix/api-client';
import { useAccess, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, FormDialog, JalaliDate, KeyValueList, LoadingState, Money, PageHeader, SelectRHF, StatusChip, TextFieldRHF } from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import { PatientRegisterDialog, type PatientRegisterInitial } from '../patients/PatientRegisterDialog';
import { useSchedulingActions } from '../scheduling/SchedulingActions';
import { consultationPath, engKeys, leadTone, LOST_REASONS, NEW_CONSULTATION_PATH, priorityTone, splitName } from './common';
import { CommunicationHistory } from './CommunicationHistory';
import { FollowUpDialog } from './FollowUpDialogs';
import { AssignLeadDialog, SlaChip } from './LeadAssignment';
import { LeadDialog } from './LeadDialog';

export const SCR_STAFF_960_DETAIL = 'SCR-STAFF-960';

export function LeadDetailPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const access = useAccess();
  const scheduling = useSchedulingActions();
  const write = useWriteAccess('eng.lead.manage');
  const [editing, setEditing] = useState(false);
  const [losing, setLosing] = useState(false);
  const [converting, setConverting] = useState(false);
  const [addingFollowUp, setAddingFollowUp] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const merged = (location.state as { merged?: boolean } | null)?.merged === true;

  const lead = useQuery({
    queryKey: engKeys.lead(session?.tenantId, id),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/engagement/leads/{id}', { params: { path: { id } } })),
  });

  const refresh = (view: EngLeadView) => {
    queryClient.setQueryData(engKeys.lead(session?.tenantId, id), view);
    void queryClient.invalidateQueries({ queryKey: engKeys.leads(session?.tenantId) });
    void queryClient.invalidateQueries({ queryKey: engKeys.queue(session?.tenantId) });
  };

  const action = useMutation({
    mutationFn: (kind: 'contact' | 'reengage') =>
      unwrap(
        kind === 'contact'
          ? tenantApi.POST('/api/v1/engagement/leads/{id}:contact', { params: { path: { id } } })
          : tenantApi.POST('/api/v1/engagement/leads/{id}:reengage', { params: { path: { id } } }),
      ),
    onSuccess: refresh,
  });

  const convert = useMutation({
    mutationFn: (patientId: string) => unwrap(tenantApi.POST('/api/v1/engagement/leads/{id}:convert', { params: { path: { id } }, body: { patientId } })),
    onSuccess: refresh,
  });

  const view = lead.data;
  const initial = useMemo<PatientRegisterInitial | undefined>(
    () => (view ? { ...splitName(view.fullName), mobile: view.mobile, gender: view.gender ?? 'Female', referralSourceId: view.sourceId ?? '' } : undefined),
    [view],
  );

  if (lead.isPending) {
    return <LoadingState rows={4} />;
  }

  if (lead.error || !view) {
    return <ErrorState error={lead.error} onRetry={() => void lead.refetch()} />;
  }

  const open = view.status !== 'Converted' && view.status !== 'Lost';

  return (
    <section data-screen-id={SCR_STAFF_960_DETAIL}>
      <PageHeader
        titleKey="eng:lead_detail.title"
        titleValues={{ name: view.fullName }}
        badges={
          <Stack direction="row" spacing={0.5}>
            <StatusChip tone={leadTone(view.status)} labelKey={`eng:lead_status.${view.status}`} />
            {view.isExistingPatient && <Chip size="small" color="info" variant="outlined" label={t('eng:leads.existing_patient')} data-testid="lead-existing-patient" />}
            <SlaChip lead={view} testId="lead-sla" />
          </Stack>
        }
        actions={
          write.canWrite && (
            <>
              {open && access.can('eng.lead.assign') && (
                <Button onClick={() => setAssigning(true)} data-testid="lead-assign">
                  {t('eng:lead_detail.assign')}
                </Button>
              )}
              {view.status === 'New' && (
                <Button onClick={() => action.mutate('contact')} disabled={action.isPending} data-testid="lead-contact">
                  {t('eng:lead_detail.contact')}
                </Button>
              )}
              {open && (
                <Button variant="contained" onClick={() => (view.patientId ? convert.mutate(view.patientId) : setConverting(true))} disabled={convert.isPending} data-testid="lead-convert">
                  {t('eng:lead_detail.convert')}
                </Button>
              )}
              {view.status === 'Lost' && (
                <Button onClick={() => action.mutate('reengage')} data-testid="lead-reengage">
                  {t('eng:lead_detail.reengage')}
                </Button>
              )}
              {view.status !== 'Converted' && (
                <Button onClick={() => setEditing(true)} data-testid="lead-edit">
                  {t('eng:lead_detail.edit')}
                </Button>
              )}
              {open && (
                <Button color="warning" onClick={() => setLosing(true)} data-testid="lead-lose">
                  {t('eng:lead_detail.lose')}
                </Button>
              )}
            </>
          )
        }
      />
      <Stack spacing={2}>
        {merged && (
          <Alert severity="warning" data-testid="lead-merged">
            {t('eng:lead_detail.merged')}
          </Alert>
        )}
        {open && view.slaBreached && !view.firstContactAt && (
          <Alert severity="error" data-testid="lead-sla-alert">
            {t('eng:sla.breached_alert')}
          </Alert>
        )}
        {(action.error ?? convert.error) && <ErrorState error={action.error ?? convert.error} />}
        {view.status === 'Converted' && view.patientId && (
          <Alert
            severity="success"
            data-testid="lead-converted"
            action={
              <Stack direction="row" spacing={1}>
                {scheduling?.canBook && (
                  <Button size="small" onClick={() => scheduling.openBooking({ patient: { id: view.patientId!, displayName: view.patientName ?? view.fullName } })} data-testid="lead-book">
                    {t('eng:lead_detail.book')}
                  </Button>
                )}
                <Button size="small" component={RouterLink} to={`/patients/${view.patientId}`}>
                  {t('eng:lead_detail.open_patient')}
                </Button>
              </Stack>
            }
          >
            {t('eng:lead_detail.converted', { name: view.patientName ?? view.fullName })}
          </Alert>
        )}
        <Paper variant="outlined" sx={{ p: 2 }}>
          <KeyValueList
            items={[
              { labelKey: 'eng:lead_form.mobile', value: <span dir="ltr">{faDigits(view.mobile)}</span>, testId: 'lead-mobile' },
              { labelKey: 'eng:lead_form.gender', value: view.gender ? t(`eng:gender.${view.gender}`) : '—' },
              { labelKey: 'eng:lead_form.source', value: view.sourceName ?? '—', testId: 'lead-source' },
              { labelKey: 'eng:lead_form.interest', value: view.interestName ?? '—' },
              { labelKey: 'eng:lead_form.owner', value: view.ownerName ?? '—', testId: 'lead-owner' },
              ...(view.assignmentMethod ? [{ labelKey: 'eng:lead_detail.assignment', value: t(`eng:assignment_method.${view.assignmentMethod}`), testId: 'lead-assignment-method' }] : []),
              { labelKey: 'eng:lead_detail.first_contact', value: view.firstContactAt ? <JalaliDate value={view.firstContactAt} withTime /> : '—' },
              { labelKey: 'eng:lead_detail.branch', value: view.branchName ?? '—' },
              { labelKey: 'eng:lead_form.consent', value: t(view.marketingConsent ? 'eng:lead_form.consent_yes' : 'eng:lead_form.consent_no'), testId: 'lead-consent-value' },
              { labelKey: 'eng:lead_detail.last_interaction', value: <JalaliDate value={view.lastInteractionAt} withTime /> },
              { labelKey: 'eng:lead_form.note', value: view.note ?? '—' },
              ...(view.lostReason ? [{ labelKey: 'eng:lead_detail.lost_reason', value: `${t(`eng:lost_reason.${view.lostReason}`)}${view.lostNote ? ` — ${view.lostNote}` : ''}`, testId: 'lead-lost-reason' }] : []),
            ]}
          />
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h3">{t('eng:lead_detail.consultations')}</Typography>
            {access.can('eng.consultation.manage') && view.status !== 'Lost' && (
              <Button size="small" onClick={() => navigate(`${NEW_CONSULTATION_PATH}?leadId=${view.id}`)} data-testid="lead-new-consultation">
                {t('eng:lead_detail.new_consultation')}
              </Button>
            )}
          </Stack>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {view.consultations.length === 0 && <Typography variant="body2">{t('eng:lead_detail.no_consultations')}</Typography>}
            {view.consultations.map((consultation) => (
              <Stack key={consultation.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Button size="small" component={RouterLink} to={consultationPath(consultation.id)} data-testid={`lead-consultation-${consultation.id}`}>
                  {t(`eng:consultation_type.${consultation.type}`)}
                </Button>
                <JalaliDate value={consultation.createdAt} />
                <Money amountMinor={Number(consultation.simulatedPayable.amount)} />
                {consultation.quoteId && <Chip size="small" label={t('eng:consultation.quote_status', { status: t(`eng:quote_status.${consultation.quoteStatus ?? 'Draft'}`) })} />}
              </Stack>
            ))}
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h3">{t('eng:lead_detail.followups')}</Typography>
            {access.can('eng.followup.manage') && open && (
              <Button size="small" onClick={() => setAddingFollowUp(true)} data-testid="lead-add-followup">
                {t('eng:lead_detail.add_followup')}
              </Button>
            )}
          </Stack>
          <Stack spacing={1} sx={{ mt: 1 }} divider={<Divider flexItem />}>
            {view.followUps.length === 0 && <Typography variant="body2">{t('eng:lead_detail.no_followups')}</Typography>}
            {view.followUps.map((activity) => (
              <Stack key={activity.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" data-testid={`lead-followup-${activity.id}`}>
                <StatusChip tone={priorityTone(activity.priority)} labelKey={`eng:priority.${activity.priority}`} />
                <Typography variant="body2">{activity.goal}</Typography>
                <JalaliDate value={activity.dueAt} withTime />
                <Chip size="small" variant="outlined" label={t(`eng:followup_status.${activity.status}`)} />
                {activity.outcome && <Chip size="small" label={t(`eng:followup_outcome.${activity.outcome}`)} />}
              </Stack>
            ))}
          </Stack>
        </Paper>

        <CommunicationHistory subjectKind="Lead" subjectId={view.id} />

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h3">{t('eng:lead_detail.sources')}</Typography>
          <Stack spacing={0.5} sx={{ mt: 1 }} data-testid="lead-sources">
            {view.sources.map((source) => (
              <Typography key={`${source.at}-${source.kind}`} variant="body2">
                {t(`eng:source_kind.${source.kind}`)} · {source.sourceName ?? source.sourceCode ?? '—'} · <JalaliDate value={source.at} withTime />
                {source.note ? ` · ${source.note}` : ''}
              </Typography>
            ))}
          </Stack>
        </Paper>
      </Stack>

      {editing && (
        <LeadDialog
          lead={view}
          onClose={(saved) => {
            setEditing(false);
            if (saved) {
              refresh(saved);
            }
          }}
        />
      )}
      {losing && (
        <LoseDialog
          leadId={view.id}
          onClose={(saved) => {
            setLosing(false);
            if (saved) {
              refresh(saved);
            }
          }}
        />
      )}
      <PatientRegisterDialog
        open={converting}
        initial={initial}
        onClose={() => setConverting(false)}
        onOpenPatient={(patientId) => navigate(`/patients/${patientId}`)}
        onRegistered={(patient) => {
          setConverting(false);
          convert.mutate(patient.id, {
            onSuccess: () => scheduling?.canBook && scheduling.openBooking({ patient: { id: patient.id, displayName: view.fullName } }),
          });
        }}
      />
      {assigning && (
        <AssignLeadDialog
          lead={view}
          onClose={(saved) => {
            setAssigning(false);
            if (saved) {
              refresh(saved);
            }
          }}
        />
      )}
      {addingFollowUp && <FollowUpDialog subject={{ kind: 'Lead', id: view.id, name: view.fullName }} onClose={(saved) => (setAddingFollowUp(false), saved && void lead.refetch())} />}
    </section>
  );
}

const loseSchema = z
  .object({ reason: z.string().min(1, { error: 'eng:validation.lost_reason_required' }), note: z.string().max(1000, { error: 'common:validation.max_length' }) })
  .superRefine((value, context) => {
    if (value.reason === 'Other' && !value.note.trim()) {
      context.addIssue({ code: 'custom', path: ['note'], message: 'eng:validation.lost_note_required' });
    }
  });
type LoseForm = z.input<typeof loseSchema>;

/** Lost با علت از فهرست (AC-ENG-005.2). */
function LoseDialog({ leadId, onClose }: { leadId: string; onClose: (saved?: EngLeadView) => void }) {
  const { tenantApi } = useTenantSession();
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState } = useForm<LoseForm>({ resolver: zodResolver(loseSchema), defaultValues: { reason: '', note: '' } });
  const save = useMutation({
    mutationFn: (form: LoseForm) =>
      unwrap(tenantApi.POST('/api/v1/engagement/leads/{id}:lose', { params: { path: { id: leadId } }, body: { reason: form.reason as never, note: form.note || null } })),
    onMutate: () => setSubmitError(undefined),
    onSuccess: (saved) => onClose(saved),
    onError: (error) => setSubmitError(error),
  });
  return (
    <FormDialog
      open
      maxWidth="xs"
      screenId="SCR-STAFF-960-LOST"
      titleKey="eng:lose_form.title"
      onSubmit={() => void handleSubmit((form) => save.mutate(form))()}
      onClose={() => onClose()}
      isDirty={formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <SelectRHF control={control} name="reason" labelKey="eng:lose_form.reason" required fullWidth options={LOST_REASONS.map((value) => ({ value, labelKey: `eng:lost_reason.${value}` }))} />
        <TextFieldRHF control={control} name="note" labelKey="eng:lose_form.note" multiline minRows={2} fullWidth />
      </Stack>
    </FormDialog>
  );
}
